import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import {
  exportSurveyRequest,
  exportSurveyPdfRequest,
  exportSurveyIndividualPdfRequest,
  exportSurveyIndividualXlsxRequest
} from '../../requests/surveys';
import { downloadFile } from '../../services/utils';
import QuestionResult from './ResultsLayouts/QuestionResult';
import { getAvailableOptions } from './ResultsLayouts/QuestionChartConfig';

const SurveyResults = ({ survey, setOnRequest, onRequest }) => {
  const dispatch = useDispatch();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [chartTypes, setChartTypes] = useState({});

  const hasResponses =
    (survey.employeeSurveys && survey.employeeSurveys.length > 0) ||
    survey.employeeCount > 0 ||
    survey.employeeSurveysCount > 0;

  const getQuestionPlotType = question => {
    if (chartTypes[question.id]) {
      return chartTypes[question.id];
    }
    const opts = getAvailableOptions(question);
    return opts[0]?.id || 'anillo';
  };

  const exportResults = () => {
    setOnRequest(true);
    setShowDropdown(false);

    const paramsChartTypes = {};
    (survey.questionsAttributes || []).forEach(q => {
      paramsChartTypes[q.id] = getQuestionPlotType(q);
    });

    exportSurveyRequest(survey.id, {
      dispatch,
      params: { chart_types: JSON.stringify(paramsChartTypes) },
      successCallback: downloadFile,
      callback: () => setOnRequest(false)
    });
  };

  const exportPdfResults = () => {
    setOnRequest(true);
    setShowDropdown(false);

    const paramsChartTypes = {};
    (survey.questionsAttributes || []).forEach(q => {
      paramsChartTypes[q.id] = getQuestionPlotType(q);
    });

    exportSurveyPdfRequest(survey.id, {
      dispatch,
      params: { chart_types: JSON.stringify(paramsChartTypes) },
      successCallback: downloadFile,
      callback: () => setOnRequest(false)
    });
  };

  const exportIndividualPdfResults = () => {
    setOnRequest(true);
    setShowDropdown(false);

    exportSurveyIndividualPdfRequest(survey.id, {
      dispatch,
      successCallback: downloadFile,
      callback: () => setOnRequest(false)
    });
  };

  const exportIndividualXlsxResults = () => {
    setOnRequest(true);
    setShowDropdown(false);

    exportSurveyIndividualXlsxRequest(survey.id, {
      dispatch,
      successCallback: downloadFile,
      callback: () => setOnRequest(false)
    });
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="survey-results-container">
      <Row className="mt-3 mb-2">
        <Col md={2} className="position-relative mb-2" ref={dropdownRef}>
          <Button
            variant="success"
            block
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={onRequest}
            className="export-results-btn"
          >
            {onRequest ? 'Exportando...' : 'EXPORTAR RESULTADOS'}
          </Button>

          {showDropdown && (
            <div className="export-dropdown-card">
              <div className="export-dropdown-section">
                <div className="export-dropdown-header">EXCEL</div>
                <div
                  className="export-dropdown-item"
                  onClick={exportResults}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      exportResults();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="export-dropdown-icon-box excel-box" />
                  <div className="export-dropdown-text-box">
                    <span className="export-dropdown-title">Resultados (.xlsx)</span>
                    <span className="export-dropdown-subtitle">Gráficos y tablas por pregunta</span>
                  </div>
                </div>
                <div
                  className={`export-dropdown-item ${!hasResponses ? 'disabled' : ''}`}
                  onClick={hasResponses ? exportIndividualXlsxResults : undefined}
                  onKeyDown={e => {
                    if (hasResponses && (e.key === 'Enter' || e.key === ' ')) {
                      exportIndividualXlsxResults();
                    }
                  }}
                  role="button"
                  tabIndex={hasResponses ? 0 : -1}
                >
                  <div className="export-dropdown-icon-box excel-box" />
                  <div className="export-dropdown-text-box">
                    <span className="export-dropdown-title">Respuestas individuales (.xlsx)</span>
                    <span className="export-dropdown-subtitle">Detalle por participante</span>
                  </div>
                </div>
              </div>

              <div className="export-dropdown-divider" />

              <div className="export-dropdown-section">
                <div className="export-dropdown-header">PDF</div>
                <div
                  className="export-dropdown-item"
                  onClick={exportPdfResults}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      exportPdfResults();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="export-dropdown-icon-box pdf-box" />
                  <div className="export-dropdown-text-box">
                    <span className="export-dropdown-title">Resultados (.pdf)</span>
                    <span className="export-dropdown-subtitle">Una página por pregunta</span>
                  </div>
                </div>
                <div
                  className={`export-dropdown-item ${!hasResponses ? 'disabled' : ''}`}
                  onClick={hasResponses ? exportIndividualPdfResults : undefined}
                  onKeyDown={e => {
                    if (hasResponses && (e.key === 'Enter' || e.key === ' ')) {
                      exportIndividualPdfResults();
                    }
                  }}
                  role="button"
                  tabIndex={hasResponses ? 0 : -1}
                >
                  <div className="export-dropdown-icon-box pdf-box" />
                  <div className="export-dropdown-text-box">
                    <span className="export-dropdown-title">Respuestas individuales (.pdf)</span>
                    <span className="export-dropdown-subtitle">Tabla general por participante</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
      <Row>
        {(survey.questionsAttributes || [])
          .sort((a, b) => a.position - b.position)
          .map((question, index) => (
            <Col md={6} key={`question-result-${question.id || index}`} className="mb-4 d-flex">
              <QuestionResult
                question={question}
                index={index}
                plotType={getQuestionPlotType(question)}
                setPlotType={type => setChartTypes(prev => ({ ...prev, [question.id]: type }))}
              />
            </Col>
          ))}
      </Row>
    </div>
  );
};

export default SurveyResults;
